import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
    { to: "/", label: "Home", testid: "nav-link-home" },
    { to: "/fly-fishing", label: "Fly Fishing", testid: "nav-link-fly-fishing" },
    { to: "/checklists", label: "Checklists", testid: "nav-link-checklists" },
    { to: "/journal", label: "Journal", testid: "nav-link-journal" },
    { to: "/gear", label: "Gear", testid: "nav-link-gear" },
];

export default function HeaderNav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            className={`fixed top-0 w-full z-50 transition-colors duration-500 border-b ${
                scrolled ? "bg-[#0B1320]/85 backdrop-blur-md border-white/10" : "bg-[#0B1320]/40 backdrop-blur-sm border-white/5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
                    <img
                        src="/logo-teal.jpg"
                        alt="Trout Start logo"
                        className="h-14 w-14 rounded-full ring-2 ring-teal/40 group-hover:ring-teal transition-shadow duration-300 object-cover"
                    />
                    <div className="leading-tight">
                        <span className="font-serif text-xl font-bold tracking-tight text-slate-50 block">Trout Start</span>
                        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-teal">TroutStart.com</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `relative px-4 py-2 font-sans text-sm tracking-wide transition-colors duration-300 rounded-full ${
                                    isActive ? "text-teal" : "text-slate-300 hover:text-slate-50"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute inset-x-4 -bottom-0.5 h-px bg-teal"
                                            transition={{ type: "spring", stiffness: 380, damping: 32 }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                    <Link
                        to="/checklists"
                        data-testid="nav-cta-checklist"
                        className="ml-3 px-5 py-2.5 rounded-full bg-teal text-[#0B1320] font-sans text-sm font-semibold hover:bg-coral hover:text-white transition-colors duration-300"
                    >
                        Pack Your Kit
                    </Link>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 text-slate-200"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden bg-[#0B1320]/95 backdrop-blur-md border-t border-white/10"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`${l.testid}-mobile`}
                                        className={({ isActive }) =>
                                            `font-serif text-2xl ${isActive ? "text-teal" : "text-slate-200"}`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
